import AlphaFilters from '../abstracts/classes/alpha-filters'
import AlphaHelpers from '../abstracts/classes/alpha-helpers'

class SvgComponent extends HTMLElement {
  constructor() {
    super()

    this._initialized = false
  }

  connectedCallback() {
    if (!this._isInitialized()) {
      this._init()
    }

    if (this.canBeLoaded()) {
      this._loadContent()
    }
  }

  canBeLoaded() {
    if (!this._isInitialized()) {
      return false
    }

    const config = SvgComponentLoader.getConfig()
    const elements = SvgComponentLoader.getElementsToLoad(this.getAttribute('src'))
    const connectedElements = [...elements].filter((e) => e.hasAttribute(config.attrNames.connected))

    return elements.length === connectedElements.length
  }

  _isInitialized() {
    return this._initialized
  }

  _init() {
    const config = SvgComponentLoader.getConfig()
    const copyMode = SvgComponentLoader.getCopyMode(this)
    this.setAttribute(config.attrNames.connected, 'true')
    this.setAttribute(config.attrNames.copyMode, copyMode)
    this._initialized = true
  }

  async _loadContent() {
    try {
      const config = SvgComponentLoader.getConfig()
      const src = this.getAttribute('src')
      const svgInfo = await SvgComponentLoader.getSvgInfo(src)

      // get elements to load and sort them by copy mode (standalone will be loaded first)
      const elementsToLoad = [...SvgComponentLoader.getElementsToLoad(src)].sort((a, b) => {
        const aCopyMode = parseInt(a.getAttribute(config.attrNames.copyMode), 10)
        const bCopyMode = parseInt(b.getAttribute(config.attrNames.copyMode), 10)

        return Math.sign(aCopyMode - bCopyMode)
      })

      // try to get svg reference element (an already loaded svg with this src), then load all elements async
      let svgRefEl = SvgComponentLoader.getSvgReferenceElement(src)
      for (const element of elementsToLoad) {
        // ensure that this element isn't already loading / loaded
        // SvgComponentLoader.getElementsToLoad() already checks that but in case of simultaneous calls, it can fail
        if (element.classList.contains('loading') || element.classList.contains('loaded')) {
          continue
        }

        element.classList.add('loading')

        // eslint-disable-next-line no-await-in-loop
        const svgEl = await SvgComponentLoader._loadElementContent(element, svgInfo, svgRefEl)
        if (svgEl) {
          element.classList.add('loaded')
          element.classList.remove('loading')
          element.append(svgEl)

          if (!svgRefEl) {
            svgRefEl = svgEl
            SvgComponentLoader._setSvgReferenceElement(src, svgEl)
          }

          // inform external components that this svg is loaded
          AlphaFilters.doAction('svgComponent.svg.loaded', svgEl, element)
        }
      }
    } catch (e) {
      console.error(e)
    }
  }
}

class SvgComponentLoader {
  static _svgSrcToInfo = new Map()
  static _svgSrcToRefElement = new Map()

  static getConfig() {
    const copyModes = {
      standalone: 1,
      use: 2
    }

    return {
      copyModes,
      defaultCopyMode: copyModes.use,
      attrNames: {
        connected: 'data-connected',
        copyMode: 'data-copy-mode'
      }
    }
  }

  static getAllowedCopyModes() {
    const config = this.getConfig()

    return Object.values(config.copyModes)
  }

  static getCopyMode(el) {
    const config = this.getConfig()
    const allowedCopyModes = this.getAllowedCopyModes()
    let mode = null
    const attrMode = el.getAttribute(config.attrNames.copyMode)
    if (attrMode) {
      mode = parseInt(attrMode, 10)
    }

    // allow filtering of copy mode
    mode = parseInt(AlphaFilters.applyFilters('svgComponent.copyMode', mode, el), 10)
    if (!allowedCopyModes.includes(mode)) {
      mode = config.defaultCopyMode
    }

    return mode
  }

  static async getSvgInfo(src) {
    if (!this._svgSrcToInfo.has(src)) {
      const url = await this.getSvgUrl(src)
      const content = await this.getSvgContent(src, url)

      this._svgSrcToInfo.set(src, {
        url,
        content
      })
    }

    return this._svgSrcToInfo.get(src)
  }

  static getSvgUrl(src) {
    return new Promise(async (resolve, reject) => {
      const svgInfo = this._svgSrcToInfo.get(src)
      if (svgInfo && svgInfo.url) {
        resolve(svgInfo.url)
      }

      if (AlphaHelpers.isAbsoluteUrl(src)) {
        // handle absolute urls
        if (src.indexOf('.svg') !== -1) {
          resolve(src)
        }
      } else if (src.indexOf('build/images/') !== -1) {
        // handle relative urls
        src = src.replace('build/images/', '')

        const childImport = () => import(
          /* webpackInclude: /\.svg$/ */
          /* webpackPreload: true */
          /* webpackMode: "eager" */
          `../../../../../../assets/images/${src}`)
        const parentImport = () => import(
          /* webpackInclude: /\.svg$/ */
          /* webpackPreload: true */
          /* webpackMode: "eager" */
          `../../../assets/images/${src}`)
        const result = await Promise.allSettled([childImport(), parentImport()])
        const fulfilled = result.filter((p) => p.status === 'fulfilled')
        const rejected = result.filter((p) => p.status === 'rejected')
        if (fulfilled.length) {
          const url = fulfilled.map((p) => p.value)[0].default
          resolve(url)
        } else {
          const errorMessage = rejected.map((p) => p.reason)[0]
          reject(errorMessage)
        }
      }
    })
  }

  static getSvgContent(src, url) {
    return new Promise((resolve, reject) => {
      const svgInfo = this._svgSrcToInfo.get(src)
      if (svgInfo && svgInfo.content) {
        resolve(svgInfo.content)
      }

      try {
        fetch(url)
          .then((result) => resolve(result.text()))
      } catch (e) {
        reject(e)
      }
    })
  }

  static getElementsToLoad(src) {
    const selector = `svg-component[src="${src}"]:not(.loaded):not(.loading)`

    return AlphaHelpers.smartQuerySelectorAll(selector)
  }

  static _loadElementContent(svgComponentEl, svgInfo, svgRefEl) {
    return new Promise((resolve, reject) => {
      let svgEl = svgComponentEl.querySelector('svg')
      if (svgEl) {
        resolve(svgEl)
      }

      try {
        const config = this.getConfig()
        let copyMode = svgComponentEl.getAttribute(config.attrNames.copyMode)

        // switch copy mode to standalone is this svg hasn't been loaded yet
        if (!svgRefEl) {
          copyMode = config.copyModes.standalone
          svgComponentEl.setAttribute(config.attrNames.copyMode, copyMode)
        }

        // load svg inside a template to manipulate it before we add it to DOM
        const templateEl = document.createElement('template')
        templateEl.innerHTML = svgInfo.content
        svgEl = templateEl.content.querySelector('svg')
        if (svgEl) {
          switch (parseInt(copyMode, 10)) {
            case config.copyModes.standalone:
              // build a new svg
              const maybeWrapSvgElements = (svgEl) => {
                const nonWrappedElements = svgEl.querySelectorAll('svg > *:not(defs)')
                if (nonWrappedElements.length) {
                  if (nonWrappedElements.length > 1) {
                    const wrapperEl = document.createElementNS(svgEl.getAttribute('xmlns'), 'g')
                    wrapperEl.id = `svg-wrapper-${AlphaHelpers.getUniqueId()}`
                    nonWrappedElements.forEach((el) => wrapperEl.append(el))
                    svgEl.append(wrapperEl)
                  } else {
                    const firstEl = nonWrappedElements[0]
                    if (!firstEl.hasAttribute('id')) {
                      firstEl.id = `svg-wrapper-${AlphaHelpers.getUniqueId()}`
                    }
                  }
                }
              }

              // wrap svg direct children into a <g> tag if necessary
              maybeWrapSvgElements(svgEl)
              break
            default:
              // reuse an existing svg element
              if (svgRefEl) {
                const mainChildEl = svgRefEl.querySelector('svg > [id]')
                if (mainChildEl) {
                  svgEl = svgRefEl.cloneNode(false)
                  svgEl.removeAttribute('id')
                  svgEl.removeAttribute('class')

                  const useEl = document.createElementNS('http://www.w3.org/2000/svg', 'use')
                  useEl.setAttribute('href', `#${mainChildEl.id}`)
                  svgEl.append(useEl)
                }
              }
              break
          }

          // listen to svgUniqId event to retrieve parsed svg element and return it
          const hookName = 'svgUniqId.element.parsed'
          const uniqCallbackId = AlphaHelpers.getUniqueId()

          AlphaFilters.addAction(hookName, (parsedSvgEl) => {
            if (parsedSvgEl === svgEl) {
              AlphaFilters.removeAction(hookName, uniqCallbackId)
              svgComponentEl.classList.add('svg-component-uid-parsed')
              resolve(parsedSvgEl)
            }
          }, uniqCallbackId)

          // make svg elements IDs unique
          AlphaHelpers.svgElementUniqId(svgEl, true)
        }
      } catch (e) {
        reject(e)
      }
    })
  }

  static getSvgReferenceElement(src) {
    return this._svgSrcToRefElement.get(src)
  }

  static _setSvgReferenceElement(src, svgRefEl) {
    this._svgSrcToRefElement.set(src, svgRefEl)
  }
}

customElements.define('svg-component', SvgComponent)
